import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import {
  ScanLine, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Camera, CameraOff,
  Users, UserCheck, Hourglass, Search, ScanFace
} from 'lucide-react';
import StaffTokensPanel from './StaffTokensPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCANNER_DOM_ID = 'tk-checkin-scanner';

const copy = {
  fr: {
    title: 'Pointage QR · Accueil sur place',
    subtitle: "Scannez le code QR du badge d'un participant pour l'enregistrer instantanément à l'entrée.",
    startCamera: 'Démarrer la caméra',
    stopCamera: 'Arrêter la caméra',
    cameraStarting: 'Démarrage…',
    cameraOff: 'Caméra arrêtée',
    pointBadge: 'Pointez le badge devant la caméra',
    permissionDenied: "Permission caméra refusée. Activez-la dans les réglages de votre navigateur.",
    noCamera: "Aucune caméra détectée sur cet appareil.",
    manualLabel: 'Saisie manuelle (ID badge)',
    manualPlaceholder: 'TK2026-XXXXXXXX',
    manualSubmit: 'Pointer',
    statsEligible: 'Éligibles',
    statsCheckedIn: 'Pointés',
    statsRemaining: 'Restants',
    recentTitle: 'Derniers pointages',
    noRecent: 'Aucun pointage pour le moment.',
    success: 'Bienvenue',
    alreadyTitle: 'Déjà pointé',
    alreadyDesc: 'Cette personne a déjà été enregistrée.',
    welcomeAt: 'Pointé à',
    scanCount: 'Scan n°',
    badgeIdLabel: 'Badge',
    countryLabel: 'Pays',
    statusLabel: 'Statut',
    statusPaid: 'Payé',
    statusApproved: 'Approuvé',
    statusExempted: 'Exempté',
    error: 'Erreur',
    notFound: 'Badge introuvable. Vérifiez le QR ou utilisez la saisie manuelle.',
    cooldownHint: 'Prêt pour le prochain badge dans 2 sec…',
  },
  en: {
    title: 'QR Check-in · Venue Reception',
    subtitle: "Scan an attendee's badge QR code to instantly check them in at the door.",
    startCamera: 'Start camera',
    stopCamera: 'Stop camera',
    cameraStarting: 'Starting…',
    cameraOff: 'Camera off',
    pointBadge: 'Point the badge at the camera',
    permissionDenied: 'Camera permission denied. Enable it in your browser settings.',
    noCamera: 'No camera detected on this device.',
    manualLabel: 'Manual entry (badge ID)',
    manualPlaceholder: 'TK2026-XXXXXXXX',
    manualSubmit: 'Check in',
    statsEligible: 'Eligible',
    statsCheckedIn: 'Checked in',
    statsRemaining: 'Remaining',
    recentTitle: 'Recent check-ins',
    noRecent: 'No check-ins yet.',
    success: 'Welcome',
    alreadyTitle: 'Already checked in',
    alreadyDesc: 'This attendee has already been registered.',
    welcomeAt: 'Checked in at',
    scanCount: 'Scan #',
    badgeIdLabel: 'Badge',
    countryLabel: 'Country',
    statusLabel: 'Status',
    statusPaid: 'Paid',
    statusApproved: 'Approved',
    statusExempted: 'Exempted',
    error: 'Error',
    notFound: 'Badge not found. Verify the QR or use manual entry.',
    cooldownHint: 'Ready for next badge in 2s…',
  },
};

export default function AdminCheckin({ language = 'fr', token }) {
  const t = copy[language] || copy.fr;
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [lastResult, setLastResult] = useState(null); // { ok, attendee, already_checked_in, scan_count, checked_in_at }
  const [lastError, setLastError] = useState('');
  const [stats, setStats] = useState({ eligible: 0, checked_in: 0, remaining: 0, recent: [] });
  const [manual, setManual] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const scannerRef = useRef(null);
  const lastScannedTextRef = useRef({ value: '', at: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/checkin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data || { eligible: 0, checked_in: 0, remaining: 0, recent: [] });
    } catch (_e) { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const submitCheckin = useCallback(async (payload) => {
    setSubmitting(true);
    setLastError('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/checkin`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastResult(res.data);
      // Audible feedback
      try {
        const audio = new Audio(res.data?.already_checked_in
          ? 'data:audio/wav;base64,UklGRl4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YToAAACA' // short tone
          : 'data:audio/wav;base64,UklGRl4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YToAAACA');
        audio.play().catch(() => {});
      } catch (_e) {}
      fetchStats();
    } catch (err) {
      setLastResult(null);
      setLastError(err?.response?.data?.detail || t.notFound);
    } finally {
      setSubmitting(false);
      // 2s cooldown to prevent re-scanning the same badge
      setCooldown(true);
      setTimeout(() => setCooldown(false), 2000);
    }
  }, [token, fetchStats, t.notFound]);

  const handleScanSuccess = useCallback((decodedText) => {
    if (!decodedText || cooldown) return;
    const now = Date.now();
    if (lastScannedTextRef.current.value === decodedText && now - lastScannedTextRef.current.at < 3000) {
      return; // debounce identical scans
    }
    lastScannedTextRef.current = { value: decodedText, at: now };
    submitCheckin({ raw: decodedText });
  }, [cooldown, submitCheckin]);

  const startScanner = async () => {
    setStarting(true);
    setCameraError('');
    try {
      const scanner = new Html5Qrcode(SCANNER_DOM_ID, { verbose: false });
      scannerRef.current = scanner;
      const config = {
        fps: 10,
        qrbox: (vw, vh) => {
          const min = Math.min(vw, vh);
          const size = Math.floor(min * 0.7);
          return { width: size, height: size };
        },
        aspectRatio: 1.0,
      };
      await scanner.start(
        { facingMode: 'environment' },
        config,
        handleScanSuccess,
        () => {} // ignore per-frame errors
      );
      setScanning(true);
    } catch (err) {
      const msg = String(err?.message || err || '');
      if (msg.toLowerCase().includes('permission')) setCameraError(t.permissionDenied);
      else if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('no')) setCameraError(t.noCamera);
      else setCameraError(msg);
    } finally {
      setStarting(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (_e) { /* ignore */ }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try { scannerRef.current.stop(); scannerRef.current.clear(); } catch (_e) {}
      }
    };
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const v = manual.trim();
    if (!v) return;
    submitCheckin({ badge_id: v });
    setManual('');
  };

  const lastBg = lastResult?.already_checked_in
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-emerald-50 border-emerald-300 text-emerald-900';
  const lastIcon = lastResult?.already_checked_in
    ? <AlertTriangle className="w-9 h-9 text-amber-600" />
    : <CheckCircle2 className="w-9 h-9 text-emerald-600" />;

  return (
    <div className="space-y-6" data-testid="admin-checkin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold text-[#1D1D1D] flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-[#CD0000]" />
            {t.title}
          </h2>
          <p className="text-gray-500 text-sm mt-1 max-w-xl">{t.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={fetchStats}
          data-testid="checkin-refresh-stats"
          className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 font-semibold text-sm px-3 py-2 rounded-xl transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{t.statsEligible}</p>
              <p data-testid="checkin-stat-eligible" className="text-3xl font-extrabold text-[#1D1D1D] mt-1">{stats.eligible}</p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{t.statsCheckedIn}</p>
              <p data-testid="checkin-stat-checked-in" className="text-3xl font-extrabold text-emerald-600 mt-1">{stats.checked_in}</p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{t.statsRemaining}</p>
              <p data-testid="checkin-stat-remaining" className="text-3xl font-extrabold text-amber-600 mt-1">{stats.remaining}</p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
              <Hourglass className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Scanner */}
        <div className="rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-extrabold text-[#1D1D1D] flex items-center gap-2">
              <ScanFace className="w-4 h-4 text-[#CD0000]" />
              {scanning ? t.pointBadge : t.cameraOff}
            </h3>
            <button
              type="button"
              onClick={scanning ? stopScanner : startScanner}
              disabled={starting}
              data-testid="checkin-toggle-camera"
              className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-lg transition ${
                scanning
                  ? 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                  : 'bg-[#CD0000] text-white hover:bg-[#A00000] disabled:opacity-60'
              }`}
            >
              {scanning ? <CameraOff className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
              {starting ? t.cameraStarting : (scanning ? t.stopCamera : t.startCamera)}
            </button>
          </div>

          <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
            <div id={SCANNER_DOM_ID} className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover" />
            {!scanning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2 pointer-events-none">
                <ScanLine className="w-12 h-12 opacity-40" />
                <p className="text-xs uppercase tracking-widest font-bold">{t.cameraOff}</p>
              </div>
            )}
            {/* Cooldown overlay */}
            {cooldown && scanning && (
              <div className="absolute bottom-3 left-3 right-3 bg-black/70 backdrop-blur text-white text-xs px-3 py-2 rounded-lg text-center">
                {t.cooldownHint}
              </div>
            )}
          </div>

          {cameraError && (
            <p data-testid="checkin-camera-error" className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
              {cameraError}
            </p>
          )}

          {/* Manual entry */}
          <form onSubmit={handleManualSubmit} className="mt-4 space-y-1.5" data-testid="checkin-manual-form">
            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider">{t.manualLabel}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder={t.manualPlaceholder}
                  data-testid="checkin-manual-input"
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#CD0000] uppercase font-mono tracking-wider"
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !manual.trim()}
                data-testid="checkin-manual-submit"
                className="bg-[#1D1D1D] hover:bg-black text-white font-bold text-sm px-4 rounded-xl disabled:opacity-50 transition"
              >
                {t.manualSubmit}
              </button>
            </div>
          </form>
        </div>

        {/* Last scan result + recent list */}
        <div className="space-y-4">
          {lastResult?.attendee && (
            <div data-testid="checkin-result-card" className={`rounded-2xl border-2 p-5 shadow-sm transition ${lastBg}`}>
              <div className="flex items-start gap-4">
                {lastResult.attendee.photo_url ? (
                  <img
                    src={lastResult.attendee.photo_url.startsWith('http') ? lastResult.attendee.photo_url : `${BACKEND_URL}${lastResult.attendee.photo_url}`}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/60 flex items-center justify-center shrink-0">
                    {lastIcon}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest opacity-70">
                    {lastResult.already_checked_in ? t.alreadyTitle : `${t.success} 🎉`}
                  </p>
                  <h4 className="text-xl font-extrabold mt-0.5 truncate">
                    {lastResult.attendee.first_name} {lastResult.attendee.last_name}
                  </h4>
                  <div className="text-xs mt-2 grid grid-cols-2 gap-x-4 gap-y-1 opacity-80">
                    <p><span className="font-bold">{t.badgeIdLabel}:</span> <span className="font-mono">{lastResult.attendee.badge_id}</span></p>
                    {lastResult.attendee.country && <p><span className="font-bold">{t.countryLabel}:</span> {lastResult.attendee.country}</p>}
                    <p><span className="font-bold">{t.statusLabel}:</span>{' '}
                      {lastResult.attendee.payment_exempted ? t.statusExempted : (lastResult.attendee.status === 'paid' ? t.statusPaid : t.statusApproved)}
                    </p>
                    <p><span className="font-bold">{t.scanCount}:</span> {lastResult.scan_count}</p>
                  </div>
                  {lastResult.checked_in_at && (
                    <p className="text-[11px] opacity-70 mt-2">
                      {t.welcomeAt} {new Date(lastResult.checked_in_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {lastError && (
            <div data-testid="checkin-error-card" className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 shadow-sm flex items-start gap-3">
              <XCircle className="w-7 h-7 text-rose-600 shrink-0" />
              <div>
                <p className="text-[11px] font-bold text-rose-700 uppercase tracking-widest">{t.error}</p>
                <p className="text-sm text-rose-900 mt-0.5">{lastError}</p>
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
            <h3 className="text-sm font-extrabold text-[#1D1D1D] mb-3">{t.recentTitle}</h3>
            {(stats.recent || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic">{t.noRecent}</p>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="checkin-recent-list">
                {stats.recent.map((r) => (
                  <li key={r.registration_id} className="flex items-center gap-3 py-2.5">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                      <UserCheck className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1D1D1D] truncate">
                        {r.first_name} {r.last_name}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        {r.country ? `${r.country} · ` : ''}
                        {r.checked_in_at ? new Date(r.checked_in_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                        {r.checked_in_by_staff_label ? ` · ${r.checked_in_by_staff_label}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Staff Tokens Manager (volunteer access) */}
      <StaffTokensPanel token={token} language={language} />
    </div>
  );
}
